import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Star, Home, Building2 } from "lucide-react";
import { AnnouncementBar } from "../components/layout/AnnouncementBar";
import { Header } from "../components/layout/Header";
import { Footer } from "../components/layout/Footer";
import { Overline } from "../components/SectionHeading";
import { AddressFields, emptyAddress, validateAddress } from "../components/account/AddressFields";
import { useAuth } from "../context/AuthContext";
import { listAddresses, createAddress, updateAddress, deleteAddress, setDefaultAddress } from "../services/api";

export default function AccountPage() {
  const { user, ready } = useAuth();
  const navigate = useNavigate();
  const [addresses, setAddresses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // "new" | address_id | null
  const [addr, setAddr] = useState(emptyAddress);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (ready && !user) navigate("/login?next=/account");
  }, [ready, user, navigate]);

  const load = () => {
    setLoading(true);
    listAddresses().then(setAddresses).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => {
    if (user) load();
  }, [user]);

  const openNew = () => {
    setEditing("new");
    setErrors({});
    setAddr({ ...emptyAddress, full_name: user?.name || "" });
  };
  const openEdit = (a) => {
    setEditing(a.address_id);
    setErrors({});
    setAddr({
      full_name: a.full_name || "",
      line1: a.line1 || "",
      line2: a.line2 || "",
      city: a.city || "",
      state: a.state || "",
      pincode: a.pincode || "",
      landmark: a.landmark || "",
      type: a.type || "home",
    });
  };
  const close = () => {
    setEditing(null);
    setErrors({});
  };

  const save = async () => {
    const e = validateAddress(addr);
    setErrors(e);
    if (Object.keys(e).length) return;
    setSaving(true);
    try {
      if (editing === "new") {
        await createAddress(addr);
        toast.success("Address added.");
      } else {
        await updateAddress(editing, addr);
        toast.success("Address updated.");
      }
      close();
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save address.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (a) => {
    if (!window.confirm("Delete this address?")) return;
    try {
      await deleteAddress(a.address_id);
      toast.success("Address deleted.");
      load();
    } catch {
      toast.error("Could not delete address.");
    }
  };

  const makeDefault = async (a) => {
    try {
      await setDefaultAddress(a.address_id);
      load();
    } catch {
      toast.error("Could not set default.");
    }
  };

  return (
    <div className="min-h-screen bg-brand-cream" data-testid="account-page">
      <AnnouncementBar />
      <Header />
      <main className="mx-auto max-w-4xl px-4 pb-24 pt-10 sm:px-6 lg:px-8 lg:pt-14">
        <Overline>My Account</Overline>
        <div className="mt-3 flex flex-wrap items-end justify-between gap-4">
          <h1 className="font-serif text-4xl font-medium tracking-tight text-brand-charcoal sm:text-5xl">Saved Addresses</h1>
          <button
            onClick={openNew}
            data-testid="account-add-address"
            className="inline-flex items-center gap-2 bg-brand-charcoal px-5 py-3 text-xs font-semibold uppercase tracking-[0.14em] text-brand-cream transition-colors hover:bg-brand-gold"
          >
            <Plus className="h-4 w-4" /> Add Address
          </button>
        </div>

        {loading ? (
          <p className="mt-10 text-brand-stone" data-testid="account-loading">Loading…</p>
        ) : addresses.length === 0 ? (
          <p className="mt-10 text-brand-stone" data-testid="account-empty">You have no saved addresses yet. Add one to speed up checkout.</p>
        ) : (
          <div className="mt-8 grid gap-4 sm:grid-cols-2" data-testid="account-address-list">
            {addresses.map((a) => (
              <div
                key={a.address_id}
                data-testid={`account-address-${a.address_id}`}
                className={`relative border p-5 ${a.is_default ? "border-brand-gold bg-brand-gold/5" : "border-brand-sand bg-brand-surface"}`}
              >
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-serif text-lg text-brand-charcoal">{a.full_name}</span>
                  <span className="inline-flex items-center gap-1 border border-brand-gold/40 px-2 py-0.5 text-[9px] font-semibold uppercase tracking-[0.14em] text-brand-gold">
                    {a.type === "office" ? <Building2 className="h-3 w-3" /> : <Home className="h-3 w-3" />}
                    {a.type}
                  </span>
                  {a.is_default && (
                    <span data-testid={`account-default-badge-${a.address_id}`} className="bg-brand-gold px-2 py-0.5 text-[9px] font-semibold uppercase tracking-[0.14em] text-brand-cream">
                      Default
                    </span>
                  )}
                </div>
                <p className="mt-2 text-sm leading-relaxed text-brand-stone">
                  {a.line1}
                  {a.line2 ? `, ${a.line2}` : ""}, {a.city}, {a.state} — {a.pincode}
                  {a.landmark ? ` (${a.landmark})` : ""}
                </p>
                <div className="mt-4 flex flex-wrap items-center gap-4 text-[11px] font-semibold uppercase tracking-[0.14em]">
                  {!a.is_default && (
                    <button onClick={() => makeDefault(a)} data-testid={`account-set-default-${a.address_id}`} className="inline-flex items-center gap-1 text-brand-stone transition-colors hover:text-brand-gold">
                      <Star className="h-3.5 w-3.5" /> Set default
                    </button>
                  )}
                  <button onClick={() => openEdit(a)} data-testid={`account-edit-${a.address_id}`} className="inline-flex items-center gap-1 text-brand-stone transition-colors hover:text-brand-gold">
                    <Pencil className="h-3.5 w-3.5" /> Edit
                  </button>
                  <button onClick={() => remove(a)} data-testid={`account-delete-${a.address_id}`} className="inline-flex items-center gap-1 text-brand-stone transition-colors hover:text-red-600">
                    <Trash2 className="h-3.5 w-3.5" /> Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {editing !== null && (
          <div className="fixed inset-0 z-[90] flex items-center justify-center bg-brand-charcoal/50 p-4" data-testid="account-address-modal">
            <div className="max-h-[90vh] w-full max-w-lg overflow-y-auto bg-brand-cream p-6 sm:p-8">
              <h2 className="font-serif text-2xl text-brand-charcoal">{editing === "new" ? "Add address" : "Edit address"}</h2>
              <div className="mt-6">
                <AddressFields value={addr} onChange={setAddr} errors={errors} idPrefix="account" />
              </div>
              <div className="mt-6 flex gap-3">
                <button onClick={save} disabled={saving} data-testid="account-address-save" className="flex-1 bg-brand-charcoal py-3 text-xs font-semibold uppercase tracking-[0.14em] text-brand-cream transition-colors hover:bg-brand-gold disabled:opacity-50">
                  {saving ? "Saving…" : "Save address"}
                </button>
                <button onClick={close} data-testid="account-address-cancel" className="flex-1 border border-brand-sand py-3 text-xs font-semibold uppercase tracking-[0.14em] text-brand-charcoal transition-colors hover:border-brand-gold">
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}
